import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Steps,
  Select,
  Button,
  Space,
  Table,
  Input,
  InputNumber,
  DatePicker,
  Typography,
  App,
  Alert,
  Radio,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import customerService from '@/services/customer.service';
import salesOrderService from '@/services/salesOrder.service';
import type { EligibleDeliveryLineRow } from '@/services/delivery.service';
import { useEligibleDeliveryLines, useCreateDeliveryFromWorkOrder } from '@/hooks/useDelivery';
import type { Customer } from '@/services/customer.service';
import type { SalesOrder } from '@/services/salesOrder.service';

const { TextArea } = Input;
const { Text } = Typography;

interface CreateDeliveryFromSalesOrderWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (deliveryId: string, deliveryNumber: string) => void;
}

export const CreateDeliveryFromSalesOrderWizard: React.FC<CreateDeliveryFromSalesOrderWizardProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [step, setStep] = useState(0);
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [salesOrderId, setSalesOrderId] = useState<string | undefined>();
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | undefined>();
  const [lineQty, setLineQty] = useState<Record<string, number>>({});
  const [lineUnit, setLineUnit] = useState<Record<string, number | undefined>>({});
  const [deliveryDate, setDeliveryDate] = useState(dayjs());
  const [customerPo, setCustomerPo] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');

  const { data: customers = [] } = useQuery({
    queryKey: ['customers', 'active-delivery-wizard'],
    queryFn: async () => {
      const raw = await customerService.getActiveCustomers();
      return Array.isArray(raw) ? raw : [];
    },
    enabled: open,
    staleTime: 60000,
  });

  const { data: salesOrders = [] } = useQuery({
    queryKey: ['sales-orders', 'for-delivery-wizard', customerId],
    queryFn: () =>
      salesOrderService.getAll({
        customer_id: customerId,
        approval_status: 'Approved',
      }),
    enabled: open && !!customerId,
    staleTime: 30000,
  });

  const { data: eligibleLines = [], isLoading: linesLoading } = useEligibleDeliveryLines(salesOrderId);

  const createMutation = useCreateDeliveryFromWorkOrder();

  useEffect(() => {
    if (!open) {
      setStep(0);
      setCustomerId(undefined);
      setSalesOrderId(undefined);
      setSelectedWorkOrderId(undefined);
      setLineQty({});
      setLineUnit({});
      setDeliveryDate(dayjs());
      setCustomerPo('');
      setDeliveryAddress('');
      setNotes('');
    }
  }, [open]);

  const workOrderOptions = useMemo(() => {
    const m = new Map<string, { id: string; number: string; lineCount: number }>();
    for (const row of eligibleLines) {
      const cur = m.get(row.work_order_id);
      if (!cur) {
        m.set(row.work_order_id, { id: row.work_order_id, number: row.work_order_number, lineCount: 1 });
      } else {
        cur.lineCount += 1;
      }
    }
    return Array.from(m.values());
  }, [eligibleLines]);

  useEffect(() => {
    if (step === 2 && selectedWorkOrderId) {
      const rows = eligibleLines.filter((r) => r.work_order_id === selectedWorkOrderId);
      const q: Record<string, number> = {};
      const u: Record<string, number | undefined> = {};
      rows.forEach((r) => {
        q[r.work_order_line_item_id] = r.pending;
        if (r.completed_production_units.length === 1) {
          u[r.work_order_line_item_id] = r.completed_production_units[0];
        }
      });
      setLineQty(q);
      setLineUnit(u);
    }
  }, [step, selectedWorkOrderId, eligibleLines]);

  const selectedSo = useMemo(
    () => salesOrders.find((s: SalesOrder) => s.id === salesOrderId),
    [salesOrders, salesOrderId]
  );

  const woLines = useMemo(
    () => eligibleLines.filter((r) => r.work_order_id === selectedWorkOrderId),
    [eligibleLines, selectedWorkOrderId]
  );

  const handleSubmit = async () => {
    if (!selectedWorkOrderId) return;
    const items = woLines
      .map((row) => {
        const qty = Number(lineQty[row.work_order_line_item_id] ?? 0);
        const unit = lineUnit[row.work_order_line_item_id];
        return {
          work_order_line_item_id: row.work_order_line_item_id,
          delivered_quantity: qty,
          production_unit_number:
            row.completed_production_units.length > 0 && unit != null && Number.isFinite(unit)
              ? unit
              : undefined,
        };
      })
      .filter((i) => i.delivered_quantity > 0);
    if (items.length === 0) {
      message.warning('Enter at least one line with quantity > 0');
      return;
    }
    for (const row of woLines) {
      const qty = Number(lineQty[row.work_order_line_item_id] ?? 0);
      if (qty <= 0) continue;
      if (row.completed_production_units.length > 0) {
        const u = lineUnit[row.work_order_line_item_id];
        if (u == null || !Number.isFinite(u)) {
          message.warning(`Select production unit for line ${row.line_number}`);
          return;
        }
        if (!row.completed_production_units.includes(Number(u))) {
          message.error(`Invalid unit for line ${row.line_number}`);
          return;
        }
      }
    }
    try {
      const delivery = await createMutation.mutateAsync({
        work_order_id: selectedWorkOrderId,
        delivery_date: deliveryDate.format('YYYY-MM-DD'),
        customer_po_number: customerPo || undefined,
        delivery_address: deliveryAddress || undefined,
        notes: notes || undefined,
        items,
      });
      onSuccess?.(delivery.id, delivery.delivery_note_number);
      onClose();
    } catch {
      // mutation shows error
    }
  };

  const lineColumns: ColumnsType<EligibleDeliveryLineRow> = [
    { title: 'Ln', dataIndex: 'line_number', width: 48 },
    { title: 'Item', dataIndex: 'item_code', width: 110, render: (c, r) => c || '—' },
    { title: 'Description', dataIndex: 'item_description', ellipsis: true },
    { title: 'Pending', dataIndex: 'pending', width: 72 },
    {
      title: 'Unit',
      key: 'unit',
      width: 120,
      render: (_, row) => {
        if (row.completed_production_units.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Select
            placeholder="Unit #"
            style={{ width: 100 }}
            value={lineUnit[row.work_order_line_item_id]}
            onChange={(v) => setLineUnit((prev) => ({ ...prev, [row.work_order_line_item_id]: v }))}
            options={row.completed_production_units.map((u) => ({ value: u, label: String(u) }))}
          />
        );
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 100,
      render: (_, row) => (
        <InputNumber
          min={0}
          max={row.pending}
          value={lineQty[row.work_order_line_item_id]}
          onChange={(v) =>
            setLineQty((prev) => ({ ...prev, [row.work_order_line_item_id]: Number(v ?? 0) || 0 }))
          }
        />
      ),
    },
  ];

  return (
    <Modal
      title="Create delivery note (customer order)"
      open={open}
      onCancel={onClose}
      width={920}
      footer={null}
      destroyOnClose
    >
      <Steps
        current={step}
        items={[{ title: 'Customer' }, { title: 'Customer order' }, { title: 'Work order' }, { title: 'Quantities' }]}
        style={{ marginBottom: 24 }}
      />

      {step === 0 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">Choose the customer, then the customer order with completed work to ship.</Text>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select customer"
            style={{ width: '100%' }}
            value={customerId}
            onChange={(v) => {
              setCustomerId(v);
              setSalesOrderId(undefined);
            }}
            options={customers.map((c: Customer) => ({
              value: c.id,
              label: `${c.customer_name} (${c.customer_code})`,
            }))}
          />
          <Button type="primary" disabled={!customerId} onClick={() => setStep(1)}>
            Next
          </Button>
        </Space>
      )}

      {step === 1 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button onClick={() => setStep(0)} style={{ marginBottom: 8 }}>
            Back
          </Button>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select customer order"
            style={{ width: '100%' }}
            value={salesOrderId}
            onChange={setSalesOrderId}
            options={salesOrders.map((s: SalesOrder) => ({
              value: s.id,
              label: s.sales_order_number,
            }))}
          />
          <Space>
            <Button type="primary" disabled={!salesOrderId} onClick={() => setStep(2)}>
              Next
            </Button>
          </Space>
        </Space>
      )}

      {step === 2 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button onClick={() => setStep(1)} style={{ marginBottom: 8 }}>
            Back
          </Button>
          {linesLoading ? (
            <Text>Loading eligible lines…</Text>
          ) : eligibleLines.length === 0 ? (
            <Alert
              type="warning"
              showIcon
              message="No deliverable quantity"
              description="There are no completed / released work order lines with pending quantity for this customer order. Ensure work orders are linked to this order, manufacturing stages are complete, and the inspection release note is approved."
            />
          ) : (
            <>
              <Text type="secondary">Select the work order to ship.</Text>
              <Radio.Group
                value={selectedWorkOrderId}
                onChange={(e) => setSelectedWorkOrderId(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {workOrderOptions.map((wo) => (
                  <Radio key={wo.id} value={wo.id}>
                    {wo.number} ({wo.lineCount} line{wo.lineCount === 1 ? '' : 's'} with pending qty)
                  </Radio>
                ))}
              </Radio.Group>
              <Button type="primary" disabled={!selectedWorkOrderId} onClick={() => setStep(3)}>
                Next
              </Button>
            </>
          )}
        </Space>
      )}

      {step === 3 && (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button onClick={() => setStep(2)} style={{ marginBottom: 8 }}>
            Back
          </Button>
          <Text>
            Customer order: <strong>{selectedSo?.sales_order_number}</strong>
          </Text>
          <DatePicker value={deliveryDate} onChange={(d) => setDeliveryDate(d ?? dayjs())} style={{ width: '100%' }} />
          <Input placeholder="Customer PO (optional)" value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} />
          <TextArea
            rows={2}
            placeholder="Delivery address (optional)"
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
          />
          <TextArea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Table
            rowKey="work_order_line_item_id"
            size="small"
            pagination={false}
            columns={lineColumns}
            dataSource={woLines}
          />
          <Space>
            <Button type="primary" loading={createMutation.isPending} onClick={handleSubmit}>
              Create delivery note
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Space>
        </Space>
      )}
    </Modal>
  );
};
